import { ArrowLeftIcon, ArrowTopRightIcon, Pane, Tab, TabNavigation } from "evergreen-ui"
import { matchPath, useHistory } from "react-router-dom";
import React, { useEffect } from "react";
import { ColorPalette } from "../../context/Theme"
import { TabInfo } from "./TabInfo";
import { removeJcContentContainerStylesEffect } from "../removeJcContentContainerStylesEffect";

import './styles/MultiTabView.css';

export interface MultiTabViewProps {
    header: JSX.Element | string;
    tabs: TabInfo[];
    selectedTabIndex: number;
    onTabSelected: (index: number) => void;
    fullPathFormat?: string;
    backButtonHeader?: string;
    backButtonOnClick?: () => void;
}

export const MultiTabView = <Params extends { tabId?: string }>({
    header,
    tabs,
    selectedTabIndex,
    onTabSelected,
    fullPathFormat,
    backButtonHeader,
    backButtonOnClick
}: MultiTabViewProps) => {
    const history = useHistory();

    useEffect(() => removeJcContentContainerStylesEffect(false), []);

    useEffect(() => {
        // When this prop isn't provided, it means that the URL path doesn't need to change when
        // navigating between tabs. In this case, we can skip subscribing the history listener.
        if (!fullPathFormat) {
            return;
        }

        // Subscribe to path changes to handle forward and back navigation between tabs.
        const unsubscribe = history.listen((location) => {
            // Deconstruct the URL path (i.e. /tabs/:tabId) into React router params.
            const result = matchPath<Params>(location.pathname, {
                path: fullPathFormat,
            });
            // Find the corresponding tab index based on the `tabId` param in the above result.
            const index = tabs.findIndex((tab: TabInfo) => {
                return (tab.tabId === result?.params?.tabId);
            });
            // Default to the zero-index if there is no match.
            onTabSelected((index !== -1) ? index : 0);
        });

        return () => {
            unsubscribe();
        };
    }, [fullPathFormat]);

    const renderHeader = (): JSX.Element => {
        if (typeof header === "string") {
            return (
                <h3 className={"multi-tab-heading"}>
                    {header}
                </h3>
            );
        }
        return header;
    };

    const renderBackButton = (): JSX.Element => {
        if (backButtonHeader) {
            return (
                <div onClick={backButtonOnClick} className={"multi-tab-back-button"}>
                    <ArrowLeftIcon size={13} color={ColorPalette.lightGreyText}></ArrowLeftIcon>
                    {backButtonHeader}
                </div>
            );
        }
        return <></>;
    };

    return (
        <Pane className={"multi-tab-container"}>
            <Pane className={"multi-tab-nav-container"}>
                {renderBackButton()}
                {renderHeader()}
                <TabNavigation className={"multi-tab-nav"}>
                    {tabs.map((tab: TabInfo, index: number) => (
                        <Tab
                            // If the flow requires opening in a new browser tab, we don't want to highlight the text color on click.
                            appearance={tab.openInNewTab ? "secondary" : "primary"}
                            color={tab.locked ?
                                ColorPalette.disabledText :
                                ColorPalette.primaryText
                            }
                            key={tab.label}
                            id={tab.label}
                            style={{
                                fontSize: "14px",
                                pointerEvents: tab.locked ? "none" : "auto"
                            }}
                            onSelect={() => {
                                if (tab.redirect) {
                                    // Open in a new tab if specified.
                                    if (tab.openInNewTab) {
                                        window.open(tab.redirect, "_blank");
                                        return;
                                    }

                                    // Navigate to a new URL path.
                                    if (tab.useHref) {
                                        window.location.href = window.location.origin + tab.redirect;
                                        return;
                                    }

                                    // Navigate to a new page in the app.
                                    history.push(tab.redirect);
                                } else {
                                    onTabSelected(index);
                                }
                            }}
                            isSelected={index === selectedTabIndex}
                            aria-controls={`panel-${tab.label}`}
                            paddingX={0}
                            flexShrink={0}
                        >
                            {tab.label}
                            {tab.icon ? <span style={{marginLeft: 4, display: "inline-flex", verticalAlign: "middle"}}>{tab.icon}</span> : null}
                            {" "}
                            {tab.openInNewTab ? (
                                <ArrowTopRightIcon
                                    size={13}
                                    marginLeft={5}
                                    color={
                                        tab.locked
                                            ? ColorPalette.disabledText
                                            : ColorPalette.primaryText
                                    }
                                />
                            ) : null}
                        </Tab>
                    ))}
                </TabNavigation>
            </Pane>
            <Pane className={`multi-tab-content-container ${tabs[selectedTabIndex].useDefaultLayout ? "container-default-layout" : ""}`}>
                {tabs[selectedTabIndex].component}
            </Pane>
        </Pane>
    );
}
